<script>
import bem from '@/utils/bem';
import { mapActions } from 'vuex';
import UploadImage from '@/components/ux/upload/Upload-image.vue';

const b = bem('image-upload-modal');

export default {
  components: { UploadImage },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    image: {
      type: String,
      default: '',
    },
    imageMobile: {
      type: String,
      default: '',
    },
    imageText: {
      type: String,
      default: '',
    },
    imageTextMobile: {
      type: String,
      default: '',
    },
    withImageText: {
      type: Boolean,
      default: false,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'save', 'delete'],
  data() {
    return {
      desktopFiles: [],
      mobileFiles: [],
      desktopInitial: [],
      mobileInitial: [],
      isSaving: false,
      localImageText: '',
      localImageTextMobile: '',
      uploadKey: 0,
    };
  },
  watch: {
    visible: {
      handler(val) {
        if (val) {
          this.uploadKey += 1;
          this.desktopInitial = this.image ? [this.image] : [];
          this.mobileInitial = this.imageMobile ? [this.imageMobile] : [];
          this.localImageText = this.imageText || '';
          this.localImageTextMobile = this.imageTextMobile || '';
        }
      },
      immediate: true,
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      uploadModuleImagesAction: 'pageContent/uploadModuleImages',
    }),
    handleDesktopUpdate(files) {
      this.desktopFiles = files;
    },
    handleMobileUpdate(files) {
      this.mobileFiles = files;
    },
    async handleSave() {
      this.isSaving = true;
      try {
        const result = await this.uploadModuleImagesAction({
          desktopFile: this.desktopFiles[0] || null,
          mobileFile: this.mobileFiles[0] || null,
          currentImage: this.image,
          currentImageMobile: this.imageMobile,
        });
        const saveData = { ...result };
        if (this.withImageText) {
          saveData.imageText = this.localImageText;
          saveData.imageTextMobile = this.localImageTextMobile;
        }
        this.$emit('save', saveData);
        this.resetData();
      } finally {
        this.isSaving = false;
      }
    },
    handleClose() {
      this.resetData();
      this.$emit('close');
    },
    handleDelete() {
      this.resetData();
      this.$emit('delete');
    },
    resetData() {
      this.desktopFiles = [];
      this.mobileFiles = [];
      this.desktopInitial = [];
      this.mobileInitial = [];
      this.localImageText = '';
      this.localImageTextMobile = '';
    },
  },
};
</script>

<template>
  <a-modal
    :open="visible"
    :footer="null"
    width="700px"
    title="Edit Images"
    @cancel="handleClose"
  >
    <div :class="b()">
      <div :class="b('row')">
        <div :class="b('field')">
          <span :class="b('label')">Desktop Image</span>
          <UploadImage
            :key="`desktop-${uploadKey}`"
            :is-multiple="false"
            :initial-images="desktopInitial"
            @update:files="handleDesktopUpdate"
          />
        </div>
        <div :class="b('field')">
          <span :class="b('label')">Mobile Image</span>
          <UploadImage
            :key="`mobile-${uploadKey}`"
            :is-multiple="false"
            :initial-images="mobileInitial"
            @update:files="handleMobileUpdate"
          />
        </div>
      </div>
      <div v-if="withImageText" :class="b('row')">
        <div :class="b('field')">
          <span :class="b('label')">Image Text (Desktop)</span>
          <a-textarea
            v-model:value="localImageText"
            placeholder="Enter text for desktop image"
            :auto-size="{ minRows: 2, maxRows: 4 }"
          />
        </div>
        <div :class="b('field')">
          <span :class="b('label')">Image Text (Mobile)</span>
          <a-textarea
            v-model:value="localImageTextMobile"
            placeholder="Enter text for mobile image"
            :auto-size="{ minRows: 2, maxRows: 4 }"
          />
        </div>
      </div>
      <div :class="b('actions', { withDelete: showDelete })">
        <a-button
          v-if="showDelete"
          type="primary"
          danger
          @click="handleDelete"
        >
          Delete
        </a-button>
        <div :class="b('actions-right')">
          <a-button @click="handleClose">Cancel</a-button>
          <a-button type="primary" :loading="isSaving" @click="handleSave">
            Save
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.image-upload-modal {
  &__row {
    display: flex;
    gap: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(24);
    }
  }

  &__field {
    flex: 1;
    margin-bottom: 16px;

    @include mediaMinMax(1025, $normal) {
      margin-bottom: vw-full(16);
    }
  }

  &__label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #293241;
    margin-bottom: 8px;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(14);
      margin-bottom: vw-full(8);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
      margin-top: vw-full(24);
    }

    &_withDelete {
      justify-content: space-between;
    }
  }

  &__actions-right {
    display: flex;
    gap: 12px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(12);
    }
  }
}

:deep(.ant-upload-select-picture-card) {
  width: 128px !important;
  height: 128px !important;
}

:deep(.ant-upload-list-picture-card-container) {
  width: 128px !important;
  height: 128px !important;
}
</style>
